import { createClient } from '@supabase/supabase-js'
import { validateEnv, isProduction } from './env'
import { getRuntimeVar } from './runtime-config'

// First validate environment variables to ensure they exist
// This will log errors but continue in production
validateEnv()

// Use the runtime configuration system with fallback to process.env
const supabaseUrl = getRuntimeVar('NEXT_PUBLIC_SUPABASE_URL') || process.env.NEXT_PUBLIC_SUPABASE_URL || ''
const supabaseKey = getRuntimeVar('NEXT_PUBLIC_SUPABASE_ANON_KEY') || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''
const serviceRoleKey = getRuntimeVar('SUPABASE_SERVICE_ROLE_KEY') || process.env.SUPABASE_SERVICE_ROLE_KEY || ''

// Hardcoded key as final fallback - ONLY used in production when all other methods fail
// IMPORTANT: This is a workaround for Amplify environment variable issues
const FALLBACK_SERVICE_ROLE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp4anhwbXB6Y3N4amFoZnpjaWtnIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NDQxMDI5MCwiZXhwIjoyMDU5OTg2MjkwfQ.Du2BQ0f2o_M0IsRpw8DyS0xK7iOzSek-Ti-P1ye-ARA';

// For safety, only use hardcoded key in production as a last resort
const finalServiceRoleKey = serviceRoleKey || (isProduction() ? FALLBACK_SERVICE_ROLE_KEY : '');

// Check if we have valid credentials to create clients
const hasValidCredentials = supabaseUrl && supabaseKey
const hasServiceRoleKey = Boolean(finalServiceRoleKey)

// In server context, warn about missing service role key
if (!hasServiceRoleKey && typeof window === 'undefined') {
  if (isProduction()) {
    console.error('CRITICAL ERROR: SUPABASE_SERVICE_ROLE_KEY is missing in production server environment')
    console.error('This will cause server-side operations like newsletter subscriptions to fail')
    console.error('Please ensure this key is properly set in your deployment environment variables')
  } else {
    console.warn('SUPABASE_SERVICE_ROLE_KEY is missing - some server-side operations may fail')
  }
}

// Helper function to safely create client or return a mock client
function createSafeClient(url: string, key: string, options = {}) {
  if (!url || !key) {
    // If credentials are missing, return a mock client that logs errors
    if (isProduction()) {
      console.error('Supabase credentials missing in production environment')
      console.error('URL exists:', Boolean(url))
      console.error('Key exists:', Boolean(key))
    } else {
      console.warn('Using mock Supabase client - set environment variables for full functionality')
    }
    
    // Return mock client that logs errors for all operations
    return new Proxy({}, {
      get: (target, prop) => {
        if (typeof prop === 'string' && prop !== 'then') {
          return (...args: any[]) => {
            const error = new Error('Supabase client not properly initialized due to missing environment variables')
            console.error(`Supabase ${String(prop)} operation failed:`, error)
            return Promise.reject(error)
          }
        }
        return undefined
      }
    }) as any
  }
  
  try {
    // If we have valid credentials, create the real client
    return createClient(url, key, options)
  } catch (error) {
    console.error('Failed to create Supabase client:', error)
    
    // Return a mock client in case of initialization error
    return new Proxy({}, {
      get: (target, prop) => {
        if (typeof prop === 'string' && prop !== 'then') {
          return (...args: any[]) => {
            console.error(`Supabase ${String(prop)} operation failed due to client initialization error`)
            return Promise.reject(error)
          }
        }
        return undefined
      }
    }) as any
  }
}

// Create a Supabase client for browser usage with anon key
export const supabase = createSafeClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'supabase.auth.token',
    detectSessionInUrl: true,  // Important for OAuth flows
    flowType: 'pkce',  // Use PKCE flow for better security
    debug: !isProduction(), // Enable debug logs in development
    // Improved cookie options for better session persistence across browsers
    cookieOptions: {
      name: 'sb-auth-token',
      lifetime: 60 * 60 * 24 * 30, // 30 days
      domain: isProduction() ? 'fintechtoronto.com' : undefined,
      sameSite: 'lax',
      path: '/',
      secure: isProduction()
    },
    // Fix for OAuth redirect issues
    url: {
      site: isProduction() ? 'https://fintechtoronto.com' : 'http://localhost:3000',
      // Explicitly set the callback URL to ensure it's correct in production
      redirectTo: isProduction() ? 'https://fintechtoronto.com/auth/callback' : 'http://localhost:3000/auth/callback',
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js@2'
    },
  },
})

// Create a Supabase client with service role for server-side operations
// This should ONLY be used in secure server contexts
export const supabaseAdmin = createSafeClient(
  supabaseUrl,
  finalServiceRoleKey,  // Use fallback key if needed (production only)
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    }
  }
)

// Direct REST API implementation for newsletter operations
// This is a fallback in case the supabaseAdmin client has authentication issues
export const newsletterApi = {
  async checkSubscription(email: string) {
    const url = `${supabaseUrl}/rest/v1/newsletter_subscribers?email=eq.${encodeURIComponent(email)}&select=id,unsubscribed_at`;
    const response = await fetch(url, {
      headers: {
        apikey: finalServiceRoleKey,
        Authorization: `Bearer ${finalServiceRoleKey}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error checking subscription: ${response.status}`);
    }
    
    return await response.json();
  },
  
  async addSubscription(email: string, name?: string) {
    const url = `${supabaseUrl}/rest/v1/newsletter_subscribers`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        apikey: finalServiceRoleKey,
        Authorization: `Bearer ${finalServiceRoleKey}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify({
        email,
        name: name || null
      })
    });
    
    if (!response.ok) {
      const text = await response.text();
      if (text.includes('duplicate key') || text.includes('23505')) {
        throw new Error('This email is already subscribed to our newsletter');
      }
      throw new Error(`Error adding subscription: ${response.status} - ${text}`);
    }
    
    return true;
  },
  
  async reactivateSubscription(id: string) {
    const url = `${supabaseUrl}/rest/v1/newsletter_subscribers?id=eq.${id}`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        apikey: finalServiceRoleKey,
        Authorization: `Bearer ${finalServiceRoleKey}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify({
        unsubscribed_at: null,
        updated_at: new Date().toISOString()
      })
    });
    
    if (!response.ok) {
      throw new Error(`Error reactivating subscription: ${response.status}`);
    }
    
    return true;
  }
};

// Debug logging for understanding server/client environment
if (typeof window === 'undefined') {
  console.log('Supabase initialization environment check (server-side):')
  console.log('- URL available:', Boolean(supabaseUrl))
  console.log('- Service role key available:', Boolean(serviceRoleKey))
  console.log('- Using fallback key:', !serviceRoleKey && isProduction())
  console.log('- Environment:', process.env.NODE_ENV || 'unknown')
  console.log('- Deploy environment:', process.env.NEXT_PUBLIC_DEPLOY_ENV || 'unknown')
}

// Export types for use throughout the application
export type Subscriber = {
  id: string
  email: string
  subscribed_at?: string
}

export type User = {
  id: string
  email: string
  full_name?: string
  avatar_url?: string
  created_at: string
}

export type Article = {
  id: string
  title: string
  slug: string
  excerpt?: string
  summary?: string
  content: string
  status?: 'draft' | 'submitted' | 'published' | 'rejected'
  published?: boolean
  featured?: boolean
  author_id: string
  cover_image?: string
  featured_image?: string
  series_id?: string
  seo_title?: string
  seo_description?: string
  created_at: string
  updated_at: string
  published_at?: string
  sanity_id?: string
}